import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import { Subject, takeUntil, filter } from 'rxjs';

import { Memo, AnnotationModel, Comment } from 'shared/models';
import { APIError, MemosAPIService, CommentAPIService } from 'shared/services';
import { DialogService, ToastService } from 'shared/ui';

import { AnnotationsV2Service } from '../../services/annotations-v2.service';
import { EventBusService } from '../../services/event-bus.service';
import { UIStateService } from '../../services/ui-state.service';

import { AnnotationListComponent } from '../annotation-list/annotation-list.component';
import { MemoExportDialogComponent } from '../../dialogs/memo-export-dialog/memo-export-dialog.component';
import { MemoDialogComponent } from '../../dialogs/memo-dialog/memo-dialog.component';

@Component({
  selector: 'viewer-reading-group-records-comment',
  templateUrl: './reading-group-records-comment.component.html',
  styleUrls: [
    '../annotation-list/annotation-list.component.scss',
    './reading-group-records-comment.component.scss',
  ],
})
export class ReadingGroupRecordsCommentComponent
  extends AnnotationListComponent<Memo>
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('listContainer', { static: false })
  listContainer?: ElementRef;
  @ViewChild('mobileCommentInput', { static: false })
  private _mobileCommentInputElem!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('nicknameElement', { static: false })
  private _nicknameElement?: ElementRef<HTMLDivElement>;
  @ViewChild('mobileInputForm', { static: false })
  private _mobileInputFormElem?: ElementRef<HTMLDivElement>;

  // 모바일 입력창 외부 클릭 감지
  @HostListener('document:click', ['$event'])
  public _onDocumentClick(event: MouseEvent): void {
    // 프로그래밍적으로 생성된 이벤트는 무시 (스크롤 시 _closeAllMenus에서 생성되는 이벤트)
    if (!event.isTrusted) {
      return;
    }

    // 입력창이 활성화되어 있지 않으면 무시
    if (!this.isCommentInputValid) {
      return;
    }

    const target = event.target as HTMLElement;

    // 답글달기 버튼 클릭 확인
    // 클릭된 요소가 답글달기 버튼이거나 그 자식 요소인지 확인
    const replyButton = target.closest('button');
    if (replyButton && replyButton.textContent?.includes('답글달기')) {
      return;
    }

    // mobile-input-form 요소 확인
    if (!this._mobileInputFormElem?.nativeElement) {
      return;
    }

    const mobileInputForm = this._mobileInputFormElem.nativeElement;

    // 클릭된 요소가 mobile-input-form 내부에 있는지 확인
    if (mobileInputForm.contains(target)) {
      // comment-input-notification-popup 내부 클릭은 무시
      const notificationPopup = mobileInputForm.querySelector(
        '.comment-input-notification-popup'
      );
      if (notificationPopup && notificationPopup.contains(target)) {
        return;
      }
      // mobile-input-form 내부 클릭은 무시
      return;
    }

    // 외부 클릭 시 입력 비활성화
    this._disableCommentInput();
  }

  @Input() isMine = true;
  @Input() isOwnedByUser = false;
  @Input()
  set memberId(memberId: string | undefined) {
    if (this._memberId !== memberId) {
      this._memberId = memberId;

      if (this.bid) {
        this.loadItems();
      }
    }
  }
  @Input()
  set memberName(memberName: string | undefined) {
    if (this._memberName !== memberName) {
      this._memberName = memberName;
    }
  }

  private _memberId?: string;
  private _memberName?: string;
  private _scrollTimeout?: any; // 스크롤 타이머 추가
  public _selectedCommentId?: string;

  get memberId(): string | undefined {
    return this._memberId;
  }

  get memberName(): string | undefined {
    return this._memberName;
  }

  private _unsubscriber = new Subject<void>();

  public _groupId?: number;
  public _isSingleMode = false;

  public _isCommentaryEditorMode = false;

  private _bid?: string;
  public _isLoading = false;

  private _onScroll: (() => void) | null = null; // 스크롤 이벤트 핸들러 저장

  // 모바일 입력창 관련 프로퍼티
  public isCommentInputValid = false;
  public isCommentInputNotificationActive = false;
  public commentInputValue = '';
  private annotation?: AnnotationModel;
  private _isCreatingMemo = false;
  private _isCreatingReply = false; // 답글 생성 중 플래그 추가
  private _isLoadingComment = false;
  private _textareaScrollTimeouts: Map<HTMLTextAreaElement, NodeJS.Timeout> =
    new Map();

  // 답글달기 관련 데이터
  public _isReplyCommentInputOpen = false;
  public _selectedMemo?: Memo;
  public _commentState?: 'comment' | 'subComment' | 'subSubComment';
  public _selectedComment?: any; // 답글의 답글을 위한 선택된 댓글
  public _selectedReply?: any; // 답글의 답글의 답글을 위한 선택된 답글

  // 닉네임 인덴트 캐시
  private _nicknameIndent = '0px';

  constructor(
    private _memosAPIService: MemosAPIService,
    private _toastService: ToastService,
    private _dialogService: DialogService,
    private _annotationsService: AnnotationsV2Service,
    private _eventBusService: EventBusService,
    private _uiStateService: UIStateService,
    private _commentApiService: CommentAPIService,
    private _cdr: ChangeDetectorRef
  ) {
    super();
  }

  @Input()
  set bid(bid: string | undefined) {
    if (this._bid !== bid) {
      this._bid = bid;
      if (this._bid) {
        this.loadItems();
      }
    }
  }

  get bid(): string | undefined {
    return this._bid;
  }

  override ngOnInit(): void {
    this._isCommentaryEditorMode = this._uiStateService.isCommentaryEditor;

    this._eventBusService
      .on('ContentsComponent:memoUpdated')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this.loadItems();
      });

    // 내 기록 탭이 열릴 때마다 데이터 새로고침
    this._eventBusService
      .on('RightPanelComponent:myAnnotationsGroupOpened')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        if (this.isMine && this.bid) {
          this.loadItems();
        }
      });

    this._annotationsService.state$
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((state) => {
        this._groupId = state.groupId;
      });

    this._eventBusService
      .on('MemoListComponent:memoDeleted')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((data) => {
        this._onItemsDeleted([data.memoId]);
      });

    // 메모로 스크롤 이동 이벤트 구독
    this._eventBusService
      .on('CommentRightPanelComponent:scrollToMemo')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ memoId, source }) => {
        if (source === 'memo-list') {
          this._scrollToMemo(memoId);
        }
      });

    // 전체 댓글 탭 클릭 시 선택 취소
    this._eventBusService
      .on('RightPanelComponent:opened')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ activePanel }) => {
        if (activePanel === 'comment') {
          this._deselectAll();
        }
      });

    // 새로운 텍스트 선택 시 댓글 입력창 비활성화
    this._eventBusService
      .on('ContentsComponent:select')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        if (this.isCommentInputValid) {
          this.isCommentInputValid = false;
          this.isCommentInputNotificationActive = false;
          this.commentInputValue = '';
          this._resetTextareaHeight();

          // 포커스 해제
          if (this._mobileCommentInputElem?.nativeElement) {
            this._mobileCommentInputElem.nativeElement.blur();
          }
        }
      });

    // 페이지 이동 시 모바일 입력창 비활성화
    this._eventBusService
      .on('ContentsComponent:addressChange')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this._disableCommentInput();
      });

    this._eventBusService
      .on('ContentsComponent:pageClick')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this._disableCommentInput();
      });

    // 패널 닫힐 때 입력창 비활성화
    this._eventBusService
      .on('RightPanelComponent:closed')
      .pipe(
        filter(({ activePanel }) => activePanel === 'myAnnotations'),
        takeUntil(this._unsubscriber)
      )
      .subscribe(() => {
        this._disableCommentInput();
      });

    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    // 이벤트 리스너 설정을 별도 메서드로 분리
    this._setupScrollListener();

    // 초기 닉네임 인덴트 업데이트
    setTimeout(() => {
      this._updateNicknameIndent();
    }, 0);
  }

  // 스크롤 리스너 설정 메서드 추가
  private _setupScrollListener(): void {
    // listContainer가 아직 없으면 다음 변경 감지 사이클에 다시 시도
    if (!this.listContainer?.nativeElement) {
      setTimeout(() => this._setupScrollListener(), 0);
      return;
    }

    const element = this.listContainer.nativeElement;

    // 기존 리스너가 있다면 제거 (중복 방지)
    element.removeEventListener('scroll', this._onScroll);
    element.removeEventListener('scroll', this._closeAllMenus);

    // 스크롤 이벤트 핸들러
    this._onScroll = (): void => {
      // 스크롤 중일 때 클래스 추가
      element.classList.add('is-scrolling');

      // 기존 타이머가 있으면 제거
      if (this._scrollTimeout) {
        clearTimeout(this._scrollTimeout);
      }

      // 스크롤이 멈춘 후 300ms 후에 클래스 제거
      this._scrollTimeout = setTimeout(() => {
        element.classList.remove('is-scrolling');
      }, 300);
    };

    element.addEventListener('scroll', this._onScroll);
    element.addEventListener('scroll', this._closeAllMenus);
  }

  ngOnDestroy(): void {
    this._unsubscriber.next();
    this._unsubscriber.complete();

    // 타이머 정리
    if (this._scrollTimeout) {
      clearTimeout(this._scrollTimeout);
    }

    // textarea 스크롤 타이머 정리
    this._textareaScrollTimeouts.forEach((timeout) => clearTimeout(timeout));
    this._textareaScrollTimeouts.clear();

    // 이벤트 리스너 제거
    if (this.listContainer?.nativeElement && this._onScroll) {
      this.listContainer.nativeElement.removeEventListener(
        'scroll',
        this._onScroll
      );
      this.listContainer.nativeElement.removeEventListener(
        'scroll',
        this._closeAllMenus
      );
    }
  }

  public override loadItems(): void {
    if (!this.bid) {
      return;
    }

    // 이미 데이터가 있으면 로딩 상태를 표시하지 않음
    const hasExistingData = this._items.length > 0;
    if (!hasExistingData) {
      this._isLoading = true;
    }

    this._memosAPIService
      .get(
        this.bid,
        0,
        999,
        this._isCommentaryEditorMode
          ? {
              commentary_id: this._annotationsService.getState().commentaryId,
              filter: 'theirs',
            }
          : {
              group_id: this._groupId,
              filter: this.isMine ? 'mine' : 'theirs',
            },
        this._memberId
      )
      .subscribe((response) => {
        this._isSingleMode = this._groupId == null;

        this._items = response.items.map((memo) => ({
          ...memo,
          selected: false,
          updatedDateTime: new Date(memo.created_at).getTime(),
        }));
        this._sortItems();

        this._isLoading = false;

        // 데이터 로딩 완료 후 스크롤 리스너 설정
        setTimeout(() => {
          this._setupScrollListener();
        }, 0);
      });
  }

  _onMemoDeleted(memo: Memo): void {
    this._toastService.open('댓글이 삭제되었습니다.');
    this._onItemsDeleted([memo.id]);
  }

  public _onMemoUpdated(memo: Memo, updated: Partial<Memo>): void {
    Object.keys(updated).forEach((key) => {
      (memo as any)[key] = updated[key as keyof Memo];
    });
  }

  _onDeleteSelectedItemsButtonClick(): void {
    const ids = this._getSelectedItems().map((item) => item.id);
    this._deleteItems(ids);
  }

  _onEditMenuClick(memo: Memo): void {
    if (
      this._uiStateService.isCommentaryEditor &&
      !this._uiStateService.canEditCommentary
    ) {
      this._toastService.openWarning(
        '판매 설정이 완료된 코멘터리는 수정이 불가합니다.'
      );

      return;
    }

    this._dialogService
      .open(MemoDialogComponent, {
        data: {
          data: {
            id: memo.id,
            text: memo.text,
            content: memo.content,
            pinned: memo.pinned_at != null,
          },
          type: 'commentEdit',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.deleted) {
          this._onItemsDeleted([memo.id]);
        } else if (result?.updated) {
          memo.content = result.updated.content;
          memo.updated_at = new Date().toISOString();
        }
      });
  }

  _onDeleteMenuClick(memo: Memo): void {
    this._deleteItems([memo.id]);
  }

  _onExportMenuClick(memo: Memo): void {
    this._dialogService.open(MemoExportDialogComponent, {
      data: {
        id: memo.id,
        content: memo.content,
      },
    });
  }

  private _deleteItems(ids: string[]): void {
    if (
      this._uiStateService.isCommentaryEditor &&
      !this._uiStateService.canEditCommentary
    ) {
      this._toastService.openWarning(
        '판매 설정이 완료된 코멘터리는 수정이 불가합니다.'
      );

      return;
    }

    this._annotationsService.deleteMemosById(ids).subscribe({
      next: () => {
        this._onItemsDeleted(ids);
        this._toastService.open('선택한 댓글이 삭제되었습니다.');
      },
      error: (error: APIError) => {
        this._toastService.open(error.message);
      },
    });
  }

  private _onItemsDeleted(ids: string[]): void {
    this._items = this._items.filter((item) => ids.indexOf(item.id) === -1);
    this._onItemSelectionChange();
  }

  // 특정 메모로 스크롤 이동하는 메서드 추가
  private _scrollToMemo(memoId: string): void {
    const existingFocusedMemos = document.querySelectorAll(
      'viewer-memo.focused-memo'
    );
    existingFocusedMemos.forEach((memo) => {
      memo.classList.remove('focused-memo');
    });
    // 해당 메모 요소 찾기
    const memoElement = document.querySelector(
      `viewer-memo[data-memo-id="${memoId}"]`
    );
    if (memoElement) {
      // 스크롤 가능한 부모 컨테이너 찾기 (.body 클래스)
      const scrollContainer = memoElement.closest('.body');

      if (scrollContainer) {
        // 컨테이너와 메모 요소의 위치 계산
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = memoElement.getBoundingClientRect();

        // 컨테이너의 현재 스크롤 위치
        const currentScrollTop = scrollContainer.scrollTop;

        // 메모를 컨테이너 중앙에 위치시키기 위한 스크롤 계산
        const elementOffsetFromContainer = elementRect.top - containerRect.top;
        const targetScrollTop =
          currentScrollTop +
          elementOffsetFromContainer -
          containerRect.height / 2 +
          elementRect.height / 2;

        // 부드러운 스크롤로 이동
        scrollContainer.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth',
        });
      } else {
        // 컨테이너를 찾지 못한 경우 기본 동작
        memoElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }

      // 포커스 효과를 위한 클래스 추가
      memoElement.classList.add('focused-memo');
    }
  }

  // 모든 열린 메뉴를 닫는 메서드
  private _closeAllMenus(): void {
    const backdropClickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      clientX: 0,
      clientY: 0,
    });

    // document.body에 클릭 이벤트를 발생시켜 CDK의 backdrop 클릭 처리 로직을 트리거
    document.body.dispatchEvent(backdropClickEvent);

    // 또는 더 직접적으로 overlay backdrop을 찾아서 클릭 이벤트 발생
    const overlayBackdrop = document.querySelector('.cdk-overlay-backdrop');
    if (overlayBackdrop) {
      overlayBackdrop.dispatchEvent(backdropClickEvent);
    }
  }

  // limitHeight를 계산하는 헬퍼 메서드 추가
  public _shouldLimitHeight(memo: Memo): boolean {
    if (!memo.content) {
      return false;
    }

    const lines = memo.content.split('\n');

    // lines.length > 3 이거나 content.length > 64 인 경우에만 true
    return lines.length > 3 || memo.content.length > 64;
  }

  // 모바일에서 댓글 입력창 제출 버튼
  public _onMobileCommentInputSubmit(): void {
    if (this.commentInputValue.trim() === '') {
      return;
    }

    // 댓글 입력의 경우
    if (!this._isReplyCommentInputOpen) {
      if (!this.annotation) {
        return;
      }

      // 이미 댓글 생성 중이면 무시
      if (this._isCreatingMemo) {
        return;
      }

      // 댓글 생성 시작
      this._isCreatingMemo = true;
      this._isLoadingComment = true;

      this._annotationsService
        .createMemo(this.annotation, this.commentInputValue.trim(), false)
        .subscribe({
          next: () => {
            this._disableCommentInput();
            this._eventBusService.fire('ContentsComponent:memoUpdated');
          },
          error: () => {
            // 에러 발생 시에도 플래그 해제
            this._isCreatingMemo = false;
            this._isLoadingComment = false;
          },
          complete: () => {
            // 완료 시 플래그 해제
            this._isCreatingMemo = false;
          },
        });
    } else {
      // 답글 입력의 경우
      if (!this._selectedMemo) {
        return;
      }

      // 이미 답글 생성 중이면 무시
      if (this._isCreatingReply) {
        return;
      }

      // commentState에 따라 분기 처리
      if (this._commentState === 'comment') {
        // 첫 번째 답글 생성
        // 메모 작성자에게 멘션 (자신의 메모가 아닌 경우만)
        const isMine = this._selectedMemo.is_mine === 1;
        const mentionUserId = isMine ? '' : this._selectedMemo.author.id;

        // 답글 생성 시작
        this._isCreatingReply = true;

        this._commentApiService
          .createComment(
            this._selectedMemo.id,
            this.commentInputValue.trim(),
            mentionUserId
          )
          .subscribe(
            (result) => {
              this.commentInputValue = '';
              this.isCommentInputValid = false;
              this.isCommentInputNotificationActive = false;
              this._isReplyCommentInputOpen = false;

              this._resetTextareaHeight();

              // textarea에서 포커스 제거
              if (this._mobileCommentInputElem?.nativeElement) {
                this._mobileCommentInputElem.nativeElement.blur();
              }

              // reading-mode-memo 컴포넌트에 답글 전달
              this._eventBusService.fire(
                'ReadingGroupMemoListComponent:replyCommentAdded',
                {
                  memoId: this._selectedMemo!.id,
                  comment: {
                    ...result,
                    isSubReplyOpened: 0,
                  },
                }
              );
            },
            (error) => {
              // 에러 발생 시에도 플래그 해제
              this._isCreatingReply = false;
              if (error.status === 404) {
                this._toastService.openWarning('존재하지 않는 댓글입니다.');
              }
            },
            () => {
              // 완료 시 플래그 해제
              this._isCreatingReply = false;
            }
          );
      } else if (this._commentState === 'subComment' && this._selectedComment) {
        // 답글의 답글 생성
        const isMine = this._selectedComment.is_mine === 1;
        const authorId = isMine ? '' : this._selectedComment.author.id;

        // 답글 생성 시작
        this._isCreatingReply = true;

        this._commentApiService
          .createReply(
            this._selectedMemo.id,
            this.commentInputValue.trim(),
            authorId,
            this._selectedComment.id
          )
          .subscribe(
            (result) => {
              this.commentInputValue = '';
              this.isCommentInputValid = false;
              this.isCommentInputNotificationActive = false;
              this._isReplyCommentInputOpen = false;

              this._resetTextareaHeight();

              // textarea에서 포커스 제거
              if (this._mobileCommentInputElem?.nativeElement) {
                this._mobileCommentInputElem.nativeElement.blur();
              }

              // reading-mode-memo 컴포넌트에 답글의 답글 전달
              this._eventBusService.fire(
                'ReadingGroupMemoListComponent:subReplyCommentAdded',
                {
                  memoId: this._selectedMemo!.id,
                  commentId: this._selectedComment.id,
                  reply: {
                    ...result,
                    isSubReplyOpened: 0,
                  },
                }
              );
            },
            (error) => {
              // 에러 발생 시에도 플래그 해제
              this._isCreatingReply = false;
              if (error.status === 404) {
                this._toastService.openWarning('존재하지 않는 댓글입니다.');
              }
            },
            () => {
              // 완료 시 플래그 해제
              this._isCreatingReply = false;
            }
          );
      } else if (
        this._commentState === 'subSubComment' &&
        this._selectedReply
      ) {
        // 답글의 답글의 답글 생성
        const isMine = this._selectedReply.is_mine === 1;
        const authorId = isMine ? '' : this._selectedReply.author.id;

        // 답글 생성 시작
        this._isCreatingReply = true;

        this._commentApiService
          .createReply(
            this._selectedMemo.id,
            this.commentInputValue.trim(),
            authorId,
            this._selectedComment.id
          )
          .subscribe(
            (result) => {
              this.commentInputValue = '';
              this.isCommentInputValid = false;
              this.isCommentInputNotificationActive = false;
              this._isReplyCommentInputOpen = false;

              this._resetTextareaHeight();

              // textarea에서 포커스 제거
              if (this._mobileCommentInputElem?.nativeElement) {
                this._mobileCommentInputElem.nativeElement.blur();
              }

              // reading-mode-memo 컴포넌트에 답글의 답글의 답글 전달
              this._eventBusService.fire(
                'ReadingGroupMemoListComponent:subSubReplyCommentAdded',
                {
                  memoId: this._selectedMemo!.id,
                  commentId: this._selectedComment?.id,
                  replyId: this._selectedReply.id,
                  reply: {
                    ...result,
                    isSubReplyOpened: 0,
                  },
                }
              );
            },
            (error) => {
              // 에러 발생 시에도 플래그 해제
              this._isCreatingReply = false;
              if (error.status === 404) {
                this._toastService.openWarning('존재하지 않는 댓글입니다.');
              }
            },
            () => {
              // 완료 시 플래그 해제
              this._isCreatingReply = false;
            }
          );
      } else {
        // 답글 수정
        // 이미 답글 수정 중이면 무시
        if (this._isCreatingReply) {
          return;
        }

        // 답글 수정 시작
        this._isCreatingReply = true;

        this._commentApiService
          .updateComment(
            this._selectedMemo.id,
            this.commentInputValue.trim(),
            '',
            this._selectedReply.id
          )
          .subscribe({
            next: (result) => {
              this.commentInputValue = '';
              this.isCommentInputValid = false;
              this.isCommentInputNotificationActive = false;
              this._isReplyCommentInputOpen = false;

              this._resetTextareaHeight();

              // textarea에서 포커스 제거
              if (this._mobileCommentInputElem?.nativeElement) {
                this._mobileCommentInputElem.nativeElement.blur();
              }

              this._eventBusService.fire(
                'ReadingGroupMemoListComponent:subReplyCommentChanged',
                {
                  memo: this._selectedMemo!,
                  originalReply: this._selectedReply!,
                  reply: {
                    ...result,
                  },
                }
              );
            },
            error: () => {
              // 에러 발생 시에도 플래그 해제
              this._isCreatingReply = false;
            },
            complete: () => {
              // 완료 시 플래그 해제
              this._isCreatingReply = false;
            },
          });
      }
    }
  }

  private _resetTextareaHeight(): void {
    if (this._mobileCommentInputElem?.nativeElement) {
      const textarea = this._mobileCommentInputElem.nativeElement;
      textarea.style.height = '14px';
    }
  }

  public _onCommentInputChange(): void {
    // 모바일용 textarea 높이 조절
    if (this._mobileCommentInputElem?.nativeElement) {
      const textarea = this._mobileCommentInputElem.nativeElement;
      // 높이를 내용에 맞게 자동 조절
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      // 한 줄 높이 계산 (기본 14px)
      const lineHeight = 14;
      const maxHeight = lineHeight * 5; // 5줄 최대 높이
      // 내용이 비어있거나 한 줄일 때는 14px, 최대 5줄까지
      const newHeight =
        scrollHeight <= 14 ? 14 : Math.min(scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }

  // textarea 내에서 화살표 키 사용 시 뷰어 페이지 이동 방지
  public _onTextareaArrowKey(event: any): void {
    event.stopPropagation();
  }

  // textarea 스크롤 이벤트 핸들러
  public _onTextareaScroll(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;

    // 스크롤 중일 때 클래스 추가
    textarea.classList.add('is-scrolling');

    // 기존 타이머가 있으면 제거
    const existingTimeout = this._textareaScrollTimeouts.get(textarea);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // 스크롤이 멈춘 후 300ms 후에 클래스 제거
    const timeout = setTimeout(() => {
      textarea.classList.remove('is-scrolling');
      this._textareaScrollTimeouts.delete(textarea);
    }, 300);

    this._textareaScrollTimeouts.set(textarea, timeout);
  }

  // Command/Ctrl + Enter로 저장 처리
  public _onTextareaKeyDown(event: KeyboardEvent): void {
    // ESC 키 처리: 입력 취소
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this._cancelCommentInput();
      return;
    }

    // Command + Enter (Mac) 또는 Ctrl + Enter (Windows/Linux)
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      this._onMobileCommentInputSubmit();
    }
  }

  public _cancelCommentInput(): void {
    // 답글 입력 취소 이벤트 발생
    if (this._isReplyCommentInputOpen) {
      this._eventBusService.fire(
        'ReadingGroupMemoListComponent:replyInputCancelled' as any
      );
    }
    // 댓글 입력 취소 이벤트 발생
    else if (this.isCommentInputValid) {
      this._eventBusService.fire(
        'ContentsComponent:commentInputCancelled' as any
      );
    }

    this.isCommentInputValid = false;
    this.commentInputValue = '';
    this._isReplyCommentInputOpen = false;
    this._resetTextareaHeight();

    // 하이라이트 제거 이벤트 발생
    this._eventBusService.fire(
      'ReadingGroupMemoListComponent:clearTemporaryHighlight'
    );
  }

  public _disableCommentInput(): void {
    if (this.isCommentInputValid) {
      this._eventBusService.fire(
        'ReadingGroupMemoListComponent:clearTemporaryHighlight'
      );

      // 답글 입력 취소 이벤트 발생
      if (this._isReplyCommentInputOpen) {
        this._eventBusService.fire(
          'ReadingGroupMemoListComponent:replyInputCancelled' as any
        );
      }

      this.isCommentInputValid = false;
      this.isCommentInputNotificationActive = false;
      this.commentInputValue = '';
      this._isReplyCommentInputOpen = false;
      this._resetTextareaHeight();

      // 포커스 해제
      if (this._mobileCommentInputElem?.nativeElement) {
        this._mobileCommentInputElem.nativeElement.readOnly = true;
        this._mobileCommentInputElem.nativeElement.blur();
      }
    }
  }

  // 답글달기 클릭 시 모바일 입력 폼 활성화
  public _onReplyCommentClick(data: {
    memo: Memo;
    comment: Memo;
    commentState: 'comment' | 'subComment' | 'subSubComment';
    reply?: Memo;
  }): void {
    // 입력창은 비워두고 nickname-indent div로만 멘션 표시
    this.commentInputValue = '';

    this.isCommentInputValid = true;
    this.isCommentInputNotificationActive = true;
    this._isReplyCommentInputOpen = true;

    this._selectedMemo = data.memo;
    this._selectedComment = data.comment;
    this._commentState = data.commentState;
    this._selectedReply = data.reply;

    // 답글 입력 시작 이벤트 발생
    const mentionTarget = this._getMentionTarget();
    this._eventBusService.fire(
      'ReadingGroupMemoListComponent:replyInputStarted' as any,
      {
        nickname: mentionTarget?.nickname || '',
      }
    );

    if (this._mobileCommentInputElem?.nativeElement) {
      this._mobileCommentInputElem.nativeElement.readOnly = false;
      this._mobileCommentInputElem.nativeElement.focus();
      this._resetTextareaHeight();

      // 닉네임 인덴트 업데이트를 다음 변경 감지 사이클에서 실행
      setTimeout(() => {
        this._updateNicknameIndent();
      }, 0);
    }
  }

  // 모바일에서 답글 수정하기 클릭 시 모바일 입력 폼 활성화
  public _onSubReplyCommentChanged(data: { memo: Memo; reply: Comment }): void {
    this._commentState = undefined;
    this._selectedComment = undefined;

    // 답글 내용을 가져와서 HTML 태그 제거 후 설정
    const content = data.reply.content || '';
    // HTML 태그 제거 및 줄바꿈 유지
    const textContent = content
      .replace(/<br\s*\/?>/gi, '\n') // <br> 태그를 줄바꿈으로 변환
      .replace(/<\/p>/gi, '\n') // </p> 태그를 줄바꿈으로 변환
      .replace(/<[^>]*>/g, '') // 나머지 HTML 태그 제거
      .replace(/&nbsp;/g, ' ') // &nbsp;를 공백으로 변환
      .replace(/&amp;/g, '&') // &amp;를 &로 변환
      .replace(/&lt;/g, '<') // &lt;를 <로 변환
      .replace(/&gt;/g, '>') // &gt;를 >로 변환
      .replace(/&quot;/g, '"') // &quot;를 "로 변환
      .trim();

    this.commentInputValue = textContent;
    this.isCommentInputValid = true;
    this._isReplyCommentInputOpen = true;

    this._selectedMemo = data.memo;
    this._selectedReply = data.reply;

    if (this._mobileCommentInputElem?.nativeElement) {
      this._mobileCommentInputElem.nativeElement.readOnly = false;
      this._mobileCommentInputElem.nativeElement.focus();
      this._resetTextareaHeight();

      // 내용이 설정된 후 높이 조정
      setTimeout(() => {
        this._onCommentInputChange();
        this._updateNicknameIndent();
      }, 0);
    }
  }

  // 닉네임 인덴트 업데이트
  private _updateNicknameIndent(): void {
    if (this._nicknameElement?.nativeElement) {
      const width = this._nicknameElement.nativeElement.offsetWidth + 5;
      this._nicknameIndent = `${width}px`;
      this._cdr.detectChanges();
    } else {
      this._nicknameIndent = '0px';
    }
  }

  // 멘션 대상 가져오기
  public _getMentionTarget(): any {
    if (!this._isReplyCommentInputOpen) {
      return null;
    }

    if (this._commentState === 'comment' && this._selectedMemo) {
      // 첫 번째 답글인 경우 메모 작성자에게 멘션 (자신의 메모가 아닌 경우만)
      const isMine = this._selectedMemo.is_mine === 1;
      return isMine ? null : this._selectedMemo.author;
    } else if (this._commentState === 'subComment' && this._selectedComment) {
      // 답글의 답글인 경우 댓글 작성자에게 멘션 (자신의 댓글이 아닌 경우만)
      const isMine = this._selectedComment.is_mine === 1;
      return isMine ? null : this._selectedComment.author;
    } else if (this._commentState === 'subSubComment' && this._selectedReply) {
      // 답글의 답글의 답글인 경우 답글 작성자에게 멘션 (자신의 답글이 아닌 경우만)
      const isMine = this._selectedReply.is_mine === 1;
      return isMine ? null : this._selectedReply.author;
    }

    return null;
  }

  // 닉네임 인덴트 계산 (캐시된 값 반환)
  public _getNicknameIndent(): string {
    return this._nicknameIndent;
  }

  public _onCloseCommentInputNotification(event: Event): void {
    event.stopPropagation();
    this.isCommentInputNotificationActive = false;
  }
}
